import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgForOf, NgIf, DecimalPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  TuiButtonModule,
  TuiLoaderModule,
  TuiSvgModule,
  TuiTextfieldControllerModule,
  TuiDialogService,
  TuiAlertService,
} from '@taiga-ui/core';
import {
  TuiInputModule,
  TuiCheckboxModule,
  TuiInputDateRangeModule,
  TuiPaginationModule,
  TUI_PROMPT,
  TuiPromptData,
} from '@taiga-ui/kit';
import { TuiDay, TuiDayRange } from '@taiga-ui/cdk';
import { SalesService } from '../../core/services/sales.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface Sale {
  id: string;
  date: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  paymentMethod: string;
  paymentVerified: boolean;

  // Temporary property for checkbox UI
  tempVerified?: boolean;
}

@Component({
  selector: 'app-sales',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    FormsModule,
    TuiButtonModule,
    TuiLoaderModule,
    TuiSvgModule,
    TuiTextfieldControllerModule,
    TuiInputModule,
    TuiCheckboxModule,
    TuiInputDateRangeModule,
    TuiPaginationModule,
    DecimalPipe,
    DatePipe,
  ],
  providers: [SalesService],
  templateUrl: './sales.component.html',
  styleUrls: ['./sales.component.scss'],
})
export class SalesComponent implements OnInit {
  searchText: string = '';
  dateFilter: TuiDayRange | null = null;
  showSearch = false;
  isLoading = false;
  salesList: Sale[] = [];
  filteredList: Sale[] = [];
  statusMsg = false;
  tempSearchText = '';

  pageSize = { pageIndex: 1, length: 1 };

  constructor(
    private router: Router,
    private salesService: SalesService,
    private readonly dialogs: TuiDialogService,
    private readonly alerts: TuiAlertService
  ) {}

  ngOnInit(): void {
    this.loadSales();
  }

  async loadSales(): Promise<void> {
    this.isLoading = true;
    try {
      const data = await this.salesService.getAllSales();
      const salesArray: any[] = Array.isArray(data) ? data : [];
      this.salesList = salesArray.map((sale: any) => {
        const item = sale.items?.[0] || {};
        const unitPrice = item.price || 0;
        const quantity = item.quantity || 0;
        const totalPrice = unitPrice * quantity;
        return {
          id: sale._id,
          date: sale.date || '',
          itemName: item.itemName || '',
          quantity: quantity,
          unitPrice: unitPrice,
          totalPrice: totalPrice,
          paymentMethod: sale.paymentMethod || 'N/A',
          paymentVerified: !!sale.paymentVerified,
          tempVerified: !!sale.paymentVerified,
        };
      });
      this.applyFilter();
    } catch {
      this.salesList = [];
      this.filteredList = [];
    } finally {
      this.isLoading = false;
    }
  }

  toggle(): void {
    this.showSearch = !this.showSearch;
  }

  navigateToAdd(): void {
    this.router.navigate(['/admin/sales/add-edit']);
  }

  showText(): string {
    return this.statusMsg && this.tempSearchText
      ? `Showing Results for "${this.tempSearchText}"`
      : '';
  }

  resetStatus(): void {
    this.searchText = '';
    this.dateFilter = null;
    this.tempSearchText = '';
    this.statusMsg = false;
    this.applyFilter();
  }

  search(): void {
    this.statusMsg = true;
    this.tempSearchText = this.searchText;
    this.applyFilter();
  }

  cancelSearch(event: any): void {
    if (!event) {
      this.searchText = '';
      this.applyFilter();
    }
  }

  applyFilter(): void {
    const searchLower = this.searchText.toLowerCase();
    const fromDate = this.dateFilter?.from
      ? new Date(
          this.dateFilter.from.year,
          this.dateFilter.from.month - 1,
          this.dateFilter.from.day
        )
      : null;

    const toDate = this.dateFilter?.to
      ? new Date(
          this.dateFilter.to.year,
          this.dateFilter.to.month - 1,
          this.dateFilter.to.day
        )
      : null;

    this.filteredList = this.salesList.filter((sale) => {
      const matchesText =
        sale.itemName.toLowerCase().includes(searchLower) ||
        sale.paymentMethod.toLowerCase().includes(searchLower);

      let matchesDate = true;
      const saleDate = new Date(sale.date);
      if (fromDate) matchesDate = matchesDate && saleDate >= fromDate;
      if (toDate) matchesDate = matchesDate && saleDate <= toDate;

      return matchesText && matchesDate;
    });

    this.pageSize.length = Math.ceil(this.filteredList.length / 10);
  }

  async verifyPayment(sale: Sale): Promise<void> {
    const original = sale.paymentVerified;
    sale.tempVerified = !original;

    const data: TuiPromptData = {
      content: `Are you sure you want to verify this payment? You cannot unverify it after confirmation.`,
      yes: 'Verify',
      no: 'Cancel',
    };

    this.dialogs
      .open<boolean>(TUI_PROMPT, { size: 's', data })
      .subscribe(async (response) => {
        if (response) {
          try {
            await this.salesService.updateSale(sale.id, {
              paymentVerified: true,
            });
            sale.paymentVerified = true;
            sale.tempVerified = true;
            this.alerts
              .open('<p style="color: green">Payment Verified Successfully</p>')
              .subscribe();
          } catch {
            sale.tempVerified = original;
          }
        } else {
          sale.tempVerified = original;
        }
      });
  }

  goToPage(page: number): void {
    this.pageSize.pageIndex = page + 1;
  }

  downloadTableExcel(): void {
    const salesData = this.filteredList; // Use filteredList or fetch all if needed

    // Calculate totals
    let totalQuantity = 0;
    let totalAmount = 0;

    salesData.forEach((s: any) => {
      totalQuantity += s.quantity || 0;
      totalAmount += s.totalPrice || 0;
    });

    // Prepare Excel header
    const header = [
      'Date',
      'Item Name',
      'Quantity',
      'Unit Price (LKR)',
      'Total Price (LKR)',
      'Payment Method',
      'Payment Verification',
    ];

    // Map sales data to rows
    const rows = salesData.map((s: any) => [
      s.date ? new Date(s.date).toISOString().split('T')[0] : '-',
      s.itemName || '-',
      s.quantity || 0,
      s.unitPrice || 0,
      s.totalPrice || 0,
      s.paymentMethod || '-',
      s.paymentVerified ? 'Verified' : 'Not Verified',
    ]);

    // Combine totals + rows for Excel
    const worksheetData = [
      ['Total Quantity', totalQuantity],
      ['Total Amount (LKR)', totalAmount],
      [],
      header,
      ...rows,
    ];

    // Create Excel workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales');

    // Export to Excel
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      'sales_report.xlsx'
    );
  }
}
